import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Activity,
  Search,
  Filter,
  Calendar,
  User,
  Download,
  Clock,
  Building2,
  Users,
  Shield,
  FileText,
  Eye,
  RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const AuditLogPanel = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    searchTerm: "",
    actionType: "all", // all, create, update, delete, assign
    entityType: "all", // all, subsidiary, user, role, team_member
    userId: "all",
    dateRange: "last_30" // last_7, last_30, last_90, all
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalPages: 1
  });

  useEffect(() => {
    fetchAuditLogs();
  }, [filters, pagination.page]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      if (filters.actionType !== "all") {
        params.append("action", filters.actionType);
      }
      
      if (filters.entityType !== "all") {
        params.append("entity_type", filters.entityType);
      }
      
      if (filters.userId !== "all") {
        params.append("user_id", filters.userId);
      }
      
      // Date range mapping
      const daysMap = {
        "last_7": 7,
        "last_30": 30,
        "last_90": 90,
        "all": 365
      };
      params.append("days", daysMap[filters.dateRange] || 30);
      params.append("limit", pagination.pageSize * pagination.page);
      
      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      const fetchedLogs = response.data || [];
      
      setLogs(fetchedLogs);
      setPagination({ ...pagination, totalPages: Math.ceil(fetchedLogs.length / pagination.pageSize) });
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      // Fallback to empty array on error
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const icons = {
      create: <FileText className="h-4 w-4" />,
      update: <RefreshCw className="h-4 w-4" />,
      delete: <FileText className="h-4 w-4" />,
      assign: <Users className="h-4 w-4" />,
      view: <Eye className="h-4 w-4" />
    };
    return icons[action] || <Activity className="h-4 w-4" />;
  };

  const getActionColor = (action) => {
    const colors = {
      create: "bg-green-100 text-green-700",
      update: "bg-blue-100 text-blue-700",
      delete: "bg-red-100 text-red-700",
      assign: "bg-purple-100 text-purple-700",
      view: "bg-gray-100 text-gray-700"
    };
    return colors[action] || "bg-gray-100 text-gray-700";
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      subsidiary: <Building2 className="h-4 w-4" />,
      user: <User className="h-4 w-4" />,
      role: <Shield className="h-4 w-4" />,
      team_member: <Users className="h-4 w-4" />
    };
    return icons[entityType] || <Activity className="h-4 w-4" />;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const handleExport = () => {
    const csvContent = [
      ["Timestamp", "User", "Action", "Entity Type", "Description", "IP Address"].join(","),
      ...filteredLogs.map(log => [
        new Date(log.timestamp).toLocaleString(),
        log.user_name,
        log.action,
        log.entity_type,
        `"${log.description}"`,
        log.ip_address
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit-logs-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    
    toast.success("Audit logs exported successfully");
  };

  // Apply filters
  const filteredLogs = logs.filter(log => {
    // Search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      if (!log.description.toLowerCase().includes(searchLower) &&
          !log.user_name.toLowerCase().includes(searchLower) &&
          !log.entity_name.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    // Action type filter
    if (filters.actionType !== "all" && log.action !== filters.actionType) {
      return false;
    }

    // Entity type filter
    if (filters.entityType !== "all" && log.entity_type !== filters.entityType) {
      return false;
    }

    // Date range filter
    if (filters.dateRange !== "all") {
      const logDate = new Date(log.timestamp);
      const now = new Date();
      const daysAgo = parseInt(filters.dateRange.split("_")[1]);
      const cutoffDate = new Date(now.setDate(now.getDate() - daysAgo));
      if (logDate < cutoffDate) {
        return false;
      }
    }

    return true;
  });

  // Paginate results
  const startIndex = (pagination.page - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Audit Log
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchAuditLogs} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search logs..."
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Action</label>
              <Select
                value={filters.actionType}
                onValueChange={(value) => setFilters({ ...filters, actionType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="assign">Assign</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Entity Type</label>
              <Select
                value={filters.entityType}
                onValueChange={(value) => setFilters({ ...filters, entityType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="subsidiary">Subsidiary</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="role">Role</SelectItem>
                  <SelectItem value="team_member">Team Member</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Date Range</label>
              <Select
                value={filters.dateRange}
                onValueChange={(value) => setFilters({ ...filters, dateRange: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_7">Last 7 Days</SelectItem>
                  <SelectItem value="last_30">Last 30 Days</SelectItem>
                  <SelectItem value="last_90">Last 90 Days</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => setFilters({
                  searchTerm: "",
                  actionType: "all",
                  entityType: "all",
                  userId: "all",
                  dateRange: "last_30"
                })}
                className="w-full"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Results count */}
          <div className="text-sm text-gray-500 mb-4">
            Showing {paginatedLogs.length} of {filteredLogs.length} logs
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Timeline */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-12 w-12 text-gray-300 mx-auto mb-3 animate-spin" />
              <p className="text-gray-500">Loading audit logs...</p>
            </div>
          ) : paginatedLogs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No audit logs found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {paginatedLogs.map((log, index) => (
                <div
                  key={log.id}
                  className="flex gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {/* Timeline indicator */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActionColor(log.action)}`}>
                      {getActionIcon(log.action)}
                    </div>
                    {index < paginatedLogs.length - 1 && (
                      <div className="w-0.5 h-full bg-gray-200 mt-2"></div>
                    )}
                  </div>

                  {/* Log details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="capitalize">
                            {log.action}
                          </Badge>
                          <Badge variant="secondary" className="flex items-center gap-1">
                            {getEntityIcon(log.entity_type)}
                            <span className="capitalize">{log.entity_type}</span>
                          </Badge>
                        </div>

                        <p className="text-sm font-medium text-gray-900 mb-1">
                          {log.description}
                        </p>

                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {log.user_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTimestamp(log.timestamp)}
                          </span>
                          <span className="text-gray-400">
                            IP: {log.ip_address}
                          </span>
                        </div>
                      </div>

                      <div className="text-xs text-gray-400 whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {filteredLogs.length > pagination.pageSize && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <div className="text-sm text-gray-500">
                Page {pagination.page} of {Math.ceil(filteredLogs.length / pagination.pageSize)}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                  disabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                  disabled={pagination.page >= Math.ceil(filteredLogs.length / pagination.pageSize)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogPanel;
